import React from 'react';
import { Popover } from '@headlessui/react';
import KoobLoader from '@koob/ui/src/components/KoobLoader';
import TripVersionCompareButton from './TripVersionCompareButton';
import TripVersionRow from './TripVersionRow';
import { useSwitchTripVersion } from '../../hooks/useSwitchTripVersion';
import {
  useCompareWithVersion,
  useSetCompareWithVersion,
  useSetCompareVersionTotalPrice,
} from '../../hooks/useTripStoreSelectors';
import { KoobPlaceholder } from '@koob/ui';

interface TripVersionSelectorProps {
  tripId: string;
  versions?: any[];
  currentVersion?: any;
  reloadTrip: () => void;
  translation: (key: string, options?: Record<string, any>) => string;
  // Extra buttons after the compare one (TO adds the export of a version).
  renderVersionActions?: (version: any) => React.ReactNode;
}

/**
 * The version popover of the trip header: which version is current, switch to
 * another one, or compare with it.
 */
export default function TripVersionSelector({
  tripId,
  versions,
  currentVersion,
  reloadTrip,
  translation: t,
  renderVersionActions,
}: TripVersionSelectorProps) {
  const compareWithVersion = useCompareWithVersion();
  const setCompareWithVersion = useSetCompareWithVersion();
  const setCompareVersionTotalPrice = useSetCompareVersionTotalPrice();
  const { switchToVersion, loading } = useSwitchTripVersion({
    tripId,
    reloadTrip,
  });

  return (
    <Popover className="relative h-full">
      <Popover.Button className="h-full border-none bg-transparent cursor-pointer focus:outline-none">
        <div className="h-full px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted">
          <div className="h-full flex justify-center items-center space-x-1.5">
            <i className="fa-regular fa-code-branch text-foreground" />

            <div className="flex items-center">
              {t('compose.versions.version')}{' '}
              {currentVersion?.version ?? (
                <KoobPlaceholder className="ml-1 h-5 w-4 rounded" />
              )}
            </div>

            <i className="fa-light fa-chevron-down text-xs" />
          </div>
        </div>
      </Popover.Button>

      {/* The panel only closes on an outside click, so both actions inside it
          have to close it themselves: comparing right away, switching once the
          version is loaded — the spinner stays visible until then. */}
      <Popover.Panel className="fixed mt-3 z-popover">
        {({ close }) => (
          <div className="relative bg-white h-72 w-[500px] rounded-lg shadow-k overflow-hidden flex flex-col">
            {loading && (
              <div className="absolute z-30 top-0 left-0 h-full w-full bg-black/25 flex justify-center items-center">
                <KoobLoader />
              </div>
            )}

            <div
              className={[
                'flex-1 p-3',
                !loading ? 'overflow-y-scroll' : 'overflow-y-clip',
              ].join(' ')}
            >
              {versions?.map((version) => (
                <div key={version.id} className="relative flex border-b">
                  <TripVersionRow
                    version={version}
                    label={t('compose.versions.version')}
                    byLabel={t('compose.versions.by')}
                    isCurrent={currentVersion.id === version.id}
                    isCompared={compareWithVersion?.id === version.id}
                    onSelect={async () => {
                      await switchToVersion(version);
                      close();
                    }}
                  />

                  <TripVersionCompareButton
                    version={version}
                    tip={t('compose.versions.compareButtonTooltip')}
                    active={compareWithVersion?.id === version.id}
                    onClick={(nextVersion) => {
                      setCompareVersionTotalPrice(null);
                      setCompareWithVersion(nextVersion);
                      close();
                    }}
                  />

                  {renderVersionActions?.(version)}
                </div>
              ))}
            </div>

            {/* KD-3845: what a comparison shows of unsaved changes. */}
            <p className="flex items-start gap-2 border-t px-4 py-2.5 text-xs text-gray-500">
              <i className="fa-regular fa-circle-info mt-0.5" />
              {t('compose.versions.saveBeforeCompareHint')}
            </p>
          </div>
        )}
      </Popover.Panel>
    </Popover>
  );
}
